import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { AttachmentBackupSection } from './AttachmentBackupSection';

type Backup = {
  file: string;
  size_bytes: number;
  created_at: string;
  reason: string | null;
  verified_at: string | null;
  /** null = nunca verificado. Não é o mesmo que falhou. */
  verification_ok: boolean | null;
  tables: number;
  /** 'importado' = veio de fora; o sistema não sabe de qual banco. */
  origin: string;
  /**
   * A verificação teve com o que comparar?
   *
   * `false` em backup importado: ele restaurou, mas não existe contagem de
   * origem para conferir. Verde de "verificado" nesse caso seria promessa
   * maior do que o que foi checado.
   */
  comparable: boolean | null;
  original_name: string | null;
};

type Meta = {
  total: number;
  unverified: number;
  schedule: string;
  note: string;
};

type Verification = {
  ok: boolean;
  reason: string;
  tables_checked?: number;
  divergences?: Array<{ table: string; expected: number; restored: number }>;
};

function formatSize(bytes: number): string {
  return bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1).replace('.', ',')} MB`
    : `${Math.round(bytes / 1024)} KB`;
}

function formatMoment(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Backups — RF-ADM-05.
 *
 * A tela distingue TRES estados, e a distincao e o ponto dela:
 *
 *   verificado       restaurado num banco descartavel, contagem conferida
 *   nao verificado   o arquivo existe e ninguem sabe se presta
 *   FALHOU           tentou restaurar e nao deu
 *
 * Interface que mostra so "existe backup" produz confianca sem base. O estado
 * do meio e o mais importante: ele diz que ainda nao se sabe.
 */
export function BackupPage() {
  const [busy, setBusy] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const backups = useQuery({
    queryKey: ['backups'],
    queryFn: () => api.get<{ data: Backup[]; meta: Meta }>('/backups'),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['backups'] });
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<{ data: { file: string; verification: Verification | null }; meta: { note: string } }>(
        '/backups',
        { verify: true },
      ),
    onSuccess: (res) => {
      window.alert(res.meta.note);
      refresh();
    },
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível gerar o backup.'),
  });

  /**
   * Importar um arquivo de backup — RF-ADM-05.
   *
   * O arquivo é guardado e conferido, NÃO restaurado. Subir e restaurar num
   * clique só transformaria um arrastar-e-soltar acidental na perda do banco.
   * Restaurar continua sendo o segundo passo, com confirmação escrita.
   */
  const importar = useMutation({
    mutationFn: (file: File) =>
      api.upload<{ data: { file: string; size_bytes: number }; meta: { note: string } }>(
        '/backups/import',
        file,
        setProgress,
      ),
    onSuccess: (res) => {
      window.alert(res.meta.note);
      refresh();
    },
    onError: (e) =>
      window.alert(
        e instanceof ApiError
          ? (e.fieldError('file') ?? e.message)
          : 'Não foi possível enviar o arquivo.',
      ),
    onSettled: () => {
      setProgress(null);
      // Zera o input: sem isto, escolher o MESMO arquivo de novo (depois de um
      // erro) não dispara `onChange` e o botão parece morto.
      if (fileInput.current) fileInput.current.value = '';
    },
  });

  const verify = useMutation({
    mutationFn: (file: string) =>
      api.post<{ data: Verification; meta: { note: string } }>(`/backups/${file}/verify`),
    onSuccess: (res) => {
      window.alert(res.meta.note);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro na verificação.'),
    onSettled: () => setBusy(null),
  });

  /**
   * Zerar o banco.
   *
   * Estado próprio, e não um modo do `restore`: são operações com consequências
   * diferentes, e compartilhar estado é como um clique acaba disparando o
   * outro. Aqui isso custaria o banco inteiro.
   */
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState('');

  const reset = useMutation({
    mutationFn: () =>
      api.post<{ data: { safety_backup: string }; meta: { note: string; preserved: string; session_warning: string } }>(
        '/backups/reset',
        { confirm: resetConfirm },
      ),
    onSuccess: (res) => {
      window.alert(`${res.meta.note}\n\n${res.meta.preserved}\n\n${res.meta.session_warning}`);
      queryClient.clear();
      window.location.reload();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Não foi possível zerar o banco.'),
  });

  const restore = useMutation({
    mutationFn: (file: string) =>
      api.post<{ data: { safety_backup: string }; meta: { note: string; session_warning: string } }>(
        `/backups/${file}/restore`,
        { confirm: 'RESTAURAR' },
      ),
    onSuccess: (res) => {
      window.alert(`${res.meta.note}\n\n${res.meta.session_warning}`);
      // Todo o estado em memória virou passado: o banco é outro.
      queryClient.clear();
      window.location.reload();
    },
    onError: (e) =>
      window.alert(e instanceof ApiError ? e.message : 'Não foi possível restaurar.'),
    onSettled: () => setBusy(null),
  });

  const remove = useMutation({
    mutationFn: ({ file, reason }: { file: string; reason: string }) =>
      api.delete<{ message: string }>(`/backups/${file}`, { body: { reason } }),
    onSuccess: (res) => {
      window.alert(res.message);
      refresh();
    },
    onError: (e) => window.alert(e instanceof ApiError ? e.message : 'Erro ao remover.'),
  });

  const list = backups.data?.data ?? [];
  const meta = backups.data?.meta;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Backup</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta?.schedule ?? 'Diário às 03:00, com verificação de restauração.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* O input fica escondido: o `file` nativo não tem estilo próprio e
              não aceita o mesmo tratamento visual dos outros botões. */}
          <input
            ref={fileInput}
            type="file"
            accept=".gz,application/gzip"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;

              if (!file.name.toLowerCase().endsWith('.sql.gz')) {
                window.alert(
                  'O arquivo precisa terminar em .sql.gz — é o formato que este sistema gera ' +
                  'em "Baixar".\n\nUm .sql solto ou um .zip não serve.',
                );
                e.target.value = '';
                return;
              }

              if (window.confirm(
                `Enviar "${file.name}" (${formatSize(file.size)})?\n\n` +
                'O arquivo será guardado junto dos outros backups e conferido — ele NÃO será ' +
                'restaurado agora. O banco atual continua como está.\n\n' +
                'Para usá-lo depois, clique em "restaurar" na linha dele.',
              )) {
                importar.mutate(file);
              } else {
                e.target.value = '';
              }
            }}
          />

          <button
            onClick={() => fileInput.current?.click()}
            disabled={importar.isPending}
            className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            {importar.isPending
              ? progress === null || progress === 100
                ? 'Conferindo…'
                : `Enviando ${progress}%`
              : '↑ Enviar arquivo'}
          </button>

          <button
            onClick={() => {
              if (window.confirm(
                'Gerar um backup agora?\n\n' +
                'O arquivo será criado e restaurado num banco descartável para conferir que ' +
                'restaura de verdade. Leva alguns segundos.',
              )) {
                create.mutate();
              }
            }}
            disabled={create.isPending}
            className="rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {create.isPending ? 'Gerando e conferindo…' : '+ Gerar backup agora'}
          </button>
        </div>
      </div>

      {/* Barra de progresso: um arquivo grande leva minutos e sem ela a tela
          parece travada. Só aparece durante o envio. */}
      {importar.isPending && progress !== null && (
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className="h-full rounded-full bg-indigo-600 transition-[width] duration-150"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* O aviso que importa: arquivo sem verificação não conta como backup. */}
      {meta && meta.unverified > 0 && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            {meta.unverified} backup(s) sem verificação de restauração
          </p>
          <p className="mt-1 text-amber-800/80 dark:text-amber-300/80">
            Arquivo que ninguém tentou restaurar é esperança, não backup. Use "verificar" para
            restaurá-lo num banco descartável e conferir tabela a tabela.
          </p>
        </div>
      )}

      {backups.isPending ? (
        <div className="mt-5 animate-pulse space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-slate-200 dark:bg-slate-800" />
          ))}
        </div>
      ) : backups.isError ? (
        <p className="mt-5 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
          {backups.error instanceof ApiError && backups.error.status === 403
            ? 'Você não tem permissão para ver os backups.'
            : 'Não foi possível carregar os backups.'}
        </p>
      ) : list.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-slate-300 p-10 text-center dark:border-slate-700">
          <p className="text-slate-500 dark:text-slate-400">Nenhum backup ainda.</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-slate-400">
            A rotina diária roda às 03:00. Se acabou de lançar dados que não pode perder, gere um
            agora.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-2">
          {list.map((backup) => (
            <div
              key={backup.file}
              className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium">{formatMoment(backup.created_at)}</p>

                    <StatusBadge ok={backup.verification_ok} comparable={backup.comparable} />

                    {backup.origin === 'importado' && (
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-sky-700 dark:bg-sky-950 dark:text-sky-300">
                        importado
                      </span>
                    )}

                    {backup.reason === 'agendado' && (
                      <span className="text-[10px] uppercase tracking-wide text-slate-400">
                        automático
                      </span>
                    )}
                  </div>

                  <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                    {formatSize(backup.size_bytes)}
                    {/* Importado não tem contagem de tabelas de origem — dizer
                        "0 tabelas" seria falso, então não se diz nada. */}
                    {backup.origin !== 'importado' && ` · ${backup.tables} tabelas`}
                    {backup.original_name && ` · enviado como ${backup.original_name}`}
                    {backup.verified_at && ` · conferido em ${formatMoment(backup.verified_at)}`}
                  </p>
                </div>

                <div className="flex items-center gap-3 text-[11px]">
                  {/* Link direto: o download é streaming, não passa pelo
                      cliente JSON. O cookie de sessão vai junto. */}
                  <a
                    href={`/api/v1/backups/${backup.file}/download`}
                    download
                    className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700 dark:bg-slate-700 dark:hover:bg-slate-600"
                  >
                    Baixar
                  </a>

                  <button
                    onClick={() => { setBusy(backup.file); verify.mutate(backup.file); }}
                    disabled={busy !== null}
                    className="text-slate-500 hover:underline disabled:opacity-50 dark:text-slate-400"
                  >
                    {busy === backup.file && verify.isPending ? 'conferindo…' : 'verificar'}
                  </button>

                  {/* A operação mais destrutiva do sistema. O texto não
                      suaviza: quem clica precisa saber o que perde. */}
                  <button
                    onClick={() => {
                      const confirmacao = window.prompt(
                        'RESTAURAR O BANCO A PARTIR DESTE BACKUP\n\n' +
                        `Backup de ${formatMoment(backup.created_at)}.\n\n` +
                        'Tudo que foi lançado DEPOIS dessa data será perdido.\n\n' +
                        'Antes de tocar no banco, o sistema:\n' +
                        '  1. gera um backup do estado atual\n' +
                        '  2. confere que este arquivo restaura, num banco descartável\n\n' +
                        'Digite RESTAURAR para confirmar:',
                      );

                      if (confirmacao === 'RESTAURAR') {
                        setBusy(backup.file);
                        restore.mutate(backup.file);
                      } else if (confirmacao !== null) {
                        window.alert('Confirmação incorreta. Nada foi alterado.');
                      }
                    }}
                    disabled={busy !== null}
                    className="text-amber-700 hover:underline disabled:opacity-50 dark:text-amber-500"
                  >
                    {busy === backup.file && restore.isPending ? 'restaurando…' : 'restaurar'}
                  </button>

                  <button
                    onClick={() => {
                      const reason = window.prompt(
                        'Por que este backup está sendo removido?\n\n' +
                        'O motivo fica registrado na auditoria.',
                      );

                      if (reason && reason.trim().length >= 3) {
                        remove.mutate({ file: backup.file, reason: reason.trim() });
                      }
                    }}
                    className="text-rose-600 hover:underline dark:text-rose-400"
                  >
                    remover
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/*
        ================================================================
        ZONA DE PERIGO — separada de tudo, e fechada por padrão
        ================================================================

        Fica no FIM da tela, atrás de um clique, com moldura vermelha. Não é
        decoração: é a única ação do sistema que apaga o cadastro inteiro, e ela
        não pode estar à distância de um clique distraído de "gerar backup".

        O botão só habilita quando o texto de confirmação está exato. Um
        `window.confirm` deixaria "OK" e "Cancelar" parecendo igualmente
        inofensivos — digitar ZERAR obriga a ler o que se vai fazer.
      */}
      <div className="mt-5 rounded-xl border border-rose-300 bg-rose-50/60 p-4 dark:border-rose-900/60 dark:bg-rose-950/20">
        <button
          type="button"
          onClick={() => { setResetOpen(!resetOpen); setResetConfirm(''); }}
          className="flex w-full items-center justify-between text-left"
        >
          <span className="text-sm font-semibold text-rose-800 dark:text-rose-300">
            Zerar o banco de dados
          </span>
          <span className="text-xs text-rose-600 dark:text-rose-400">
            {resetOpen ? 'fechar' : 'abrir'}
          </span>
        </button>

        {resetOpen && (
          <div className="mt-3 space-y-3 text-sm">
            <p className="text-rose-900 dark:text-rose-200">
              Apaga <strong>todos os lançamentos, baixas, transferências, contas, cartões,
              faturas, dívidas, contratos, contatos e entidades</strong>. Não tem como desfazer
              pela tela.
            </p>

            <div className="rounded-lg bg-white/70 px-3 py-2 text-xs text-slate-700 dark:bg-slate-900/60 dark:text-slate-300">
              <p className="font-medium">Continuam no sistema:</p>
              <p>
                seu usuário e a senha atual · a organização · papéis e permissões · o plano de
                contas · bancos e feriados · a trilha de auditoria
              </p>
            </div>

            <p className="text-xs text-rose-800 dark:text-rose-300">
              Exige um backup das últimas 24 horas, e outro é gerado automaticamente logo antes
              de apagar — é por ele que dá para voltar.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <label className="text-xs text-slate-600 dark:text-slate-300">
                Digite <strong>ZERAR</strong> para liberar:
              </label>
              <input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="ZERAR"
                className="input w-32 py-1 text-sm"
              />
              <button
                type="button"
                disabled={resetConfirm !== 'ZERAR' || reset.isPending}
                onClick={() => reset.mutate()}
                className="rounded-lg bg-rose-600 px-3.5 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-40"
              >
                {reset.isPending ? 'Zerando…' : 'Zerar o banco'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-300">
        <p>
          <strong>Verificar</strong> não olha o arquivo: restaura num banco descartável e compara a
          contagem de cada tabela com a origem. Um arquivo íntegro pode não restaurar — são
          perguntas diferentes.
        </p>
        <p>
          <strong>Baixar</strong> tira uma cópia integral do banco do servidor. Exige permissão
          própria e cada download fica na auditoria, com usuário e IP.
        </p>
        <p>
          <strong>Enviar arquivo</strong> traz de volta um <code>.sql.gz</code> baixado antes — de
          outra máquina, de outra instalação ou de um backup guardado fora daqui. O arquivo é
          apenas guardado e conferido: <strong>nada é restaurado no envio</strong>. Ele ganha o selo
          "restaura" em vez de "verificado" porque o sistema não sabe de qual banco ele veio — pode
          ser aberto e restaurado, mas não há contagem de origem para provar que está completo.
        </p>
        <p>
          <strong>Restaurar</strong> substitui o banco inteiro pelo conteúdo do arquivo — tudo
          lançado depois da data dele se perde. Antes de tocar no banco, o sistema gera um backup
          do estado atual e confere que o arquivo escolhido restaura. Se der engano, o caminho de
          volta é restaurar esse backup de segurança.
        </p>
        {/* Limitação declarada, não escondida. */}
        <p className="text-amber-700 dark:text-amber-400">
          O arquivo <strong>não é criptografado</strong>. CPF e CNPJ continuam cifrados, mas
          valores, descrições e contatos estão em claro. Se levar a cópia para fora desta máquina,
          proteja o destino.
        </p>
        {/* A limitação que os anexos criaram, dita onde ela importa. */}
        <p className="text-amber-700 dark:text-amber-400">
          Este backup é <strong>só do banco</strong>. Os documentos anexados aos lançamentos têm
          pacote próprio, logo abaixo — restaurar só este deixaria as fichas apontando para
          arquivos que não existem.
        </p>
      </div>

      <AttachmentBackupSection />
    </div>
  );
}

function StatusBadge({ ok, comparable }: { ok: boolean | null; comparable?: boolean | null }) {
  /**
   * "Restaura" e "verificado" NÃO são o mesmo selo.
   *
   * Um backup importado que passou na verificação provou que restaura — mas
   * não existe contagem de origem para comparar, então ninguém sabe se ele
   * está completo. Pintá-lo do mesmo verde de um backup conferido tabela a
   * tabela venderia uma garantia que não foi dada.
   */
  if (ok === true && comparable === false) {
    return (
      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-sky-700 dark:bg-sky-950 dark:text-sky-300">
        restaura
      </span>
    );
  }

  if (ok === true) {
    return (
      <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
        verificado
      </span>
    );
  }

  if (ok === false) {
    return (
      <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-rose-700 dark:bg-rose-950 dark:text-rose-300">
        falhou ao restaurar
      </span>
    );
  }

  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-amber-700 dark:bg-amber-950 dark:text-amber-300">
      não verificado
    </span>
  );
}
